<template>
  <div class="calendario-simples">
    <!-- Linha do título (opcional) + Semana/Dia à direita -->
    <div v-if="title" class="calendario-header-row">
      <h3 class="calendario-titulo">{{ title }}</h3>
      <div class="calendario-view-toggle" role="group" aria-label="Visualização">
        <button type="button" class="view-btn" :class="{ ativo: viewMode === 'semana' }"
          @click="mudarView('semana')">Semana</button>
        <button type="button" class="view-btn" :class="{ ativo: viewMode === 'mes' }"
          @click="mudarView('mes')">Dia</button>
      </div>
    </div>
    <!-- Barra tipo Google Calendar: navegação e opções -->
    <div class="calendario-toolbar" :style="{ 'justify-content': viewMode === 'semana' ? 'space-between' : 'center' }">
      <div>
        <div v-if="!title" class="calendario-view-toggle" role="group" aria-label="Visualização">
          <button type="button" class="view-btn" :class="{ ativo: viewMode === 'semana' }"
            @click="mudarView('semana')">Semana</button>
          <button type="button" class="view-btn" :class="{ ativo: viewMode === 'mes' }"
            @click="mudarView('mes')">Dia</button>
        </div>
        <div v-if="viewMode === 'semana'" class="calendario-nav-bar">
          <button type="button" class="calendario-nav" @click="semanaAnterior" aria-label="Semana anterior">‹</button>
          <span class="calendario-nav-titulo">{{ periodoSemanaTexto }}</span>
          <button type="button" class="calendario-nav" @click="proximaSemana" aria-label="Próxima semana">›</button>
        </div>
      </div>
      <div v-if="viewMode === 'semana'" class="calendario-finsemana-toggle">
        <input type="checkbox" v-model="mostrarFinsDeSemana" @change="emitPeriodoAtual" />
        <span class="calendario-finsemana-label">Mostrar fim de semana</span>
      </div>
      <div v-if="viewMode === 'mes'" class="calendario-nav-bar">
        <button type="button" class="calendario-nav" @click="mesAnterior" aria-label="Mês anterior">‹</button>
        <span class="calendario-nav-titulo">{{ formatarData(inicioMes, 'MMMM yyyy') }}</span>
        <button type="button" class="calendario-nav" @click="proximoMes" aria-label="Próximo mês">›</button>
      </div>
    </div>
    <!-- Vista Semana (segunda a sexta) -->
    <template v-if="viewMode === 'semana'">
      <div v-for="dia in diasDaSemana" :key="dia.getTime()" class="dia-semana"
        :class="{ 'dia-semana-hoje': isHoje(dia) }">
        <div class="dia-semana-data"> {{ formatarData(dia, 'EEE, d') }} <span v-if="isHoje(dia)"
            class="dia-semana-hoje-badge">Hoje</span>
        </div>
        <ul v-if="eventosPorDia.get(dia.getTime())?.length" class="lista-eventos lista-eventos-compacta">
          <li v-for="ev in eventosPorDia.get(dia.getTime())" :key="ev.id" class="evento-item evento-clicavel"
            :class="{ 'evento-dia-todo': ev.allDay }" @click="$emit('event-click', ev)">
            <span class="evento-hora">{{ textoHorario(ev) }}</span>
            <span class="evento-titulo">{{ ev.title }}</span>
            <span v-if="ev.gravacaoUrl || ev.transcricaoUrl" class="evento-anexos-google" @click.stop>
              <a v-if="ev.gravacaoUrl" :href="ev.gravacaoUrl" target="_blank" class="anexo-google-icone tipo-gravacao"
                title="Gravação" @click.stop>
                <i class="bi bi-camera-video-fill"></i>
              </a>
              <a v-if="ev.transcricaoUrl" :href="ev.transcricaoUrl" target="_blank"
                class="anexo-google-icone tipo-transcricao" title="Transcrição" @click.stop>
                <i class="bi bi-file-earmark-text-fill"></i>
              </a>
            </span>
          </li>
        </ul>
        <p v-else class="sem-eventos-inline">—</p>
      </div>
    </template>
    <!-- Vista Mês (grade com contadores e clique no dia) -->
    <template v-if="viewMode === 'mes'">
      <div class="calendario-mes-wrap">
        <div class="calendario-mes-grid">
          <div class="mes-semana-cabecalho">D</div>
          <div class="mes-semana-cabecalho">S</div>
          <div class="mes-semana-cabecalho">T</div>
          <div class="mes-semana-cabecalho">Q</div>
          <div class="mes-semana-cabecalho">Q</div>
          <div class="mes-semana-cabecalho">S</div>
          <div class="mes-semana-cabecalho">S</div>
          <template v-for="celula in gradeDoMes" :key="celula.id">
            <div v-if="celula.vazio" class="mes-dia mes-dia-vazio"></div>
            <button v-else type="button" class="mes-dia mes-dia-clicavel" :class="{
              'mes-dia-hoje': celula.hoje,
              'mes-dia-com-evento': celula.eventos,
              'mes-dia-selecionado': isDiaSelecionado(celula.date)
            }" @click="selecionarDiaMes(celula.date)">
              <span class="mes-dia-numero">{{ celula.dia }}</span>
              <span v-if="celula.eventos" class="mes-dia-badge">{{ celula.eventos }}</span>
            </button>
          </template>
        </div>
      </div>
      <div v-if="diaSelecionadoMes" class="mes-eventos-dia">
        <div class="mes-eventos-dia-titulo"> Eventos de {{ formatarData(diaSelecionadoMes, 'EEEE, d \'de\' MMMM') }}
        </div>
        <ul v-if="eventosDoDiaSelecionado.length" class="lista-eventos">
          <li v-for="ev in eventosDoDiaSelecionado" :key="ev.id" class="evento-item evento-clicavel"
            :class="{ 'evento-dia-todo': ev.allDay }" @click="$emit('event-click', ev)">
            <span class="evento-hora">{{ textoHorario(ev) }}</span>
            <span class="evento-titulo">{{ ev.title }}</span>
            <span v-if="ev.gravacaoUrl || ev.transcricaoUrl" class="evento-anexos-google" @click.stop>
              <a v-if="ev.gravacaoUrl" :href="ev.gravacaoUrl" target="_blank" class="anexo-google-icone tipo-gravacao"
                title="Gravação" @click.stop>
                <i class="bi bi-camera-video-fill"></i>
              </a>
              <a v-if="ev.transcricaoUrl" :href="ev.transcricaoUrl" target="_blank"
                class="anexo-google-icone tipo-transcricao" title="Transcrição" @click.stop>
                <i class="bi bi-file-earmark-text-fill"></i>
              </a>
            </span>
          </li>
        </ul>
        <p v-else class="sem-eventos">Nenhum evento neste dia.</p>
      </div>
    </template>
  </div>
</template>
<script>
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  isSameDay,
  addDays,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  getDate,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default {
  name: 'CalendarioSimples',
  props: {
    eventos: {
      type: Array,
      default: () => [],
    },
    /** Quando informado, exibe esta linha com título à esquerda e botões Semana/Dia à direita. */
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['periodo-change', 'event-click'],
  data() {
    const hoje = new Date();
    const diaDaSemana = hoje.getDay(); // 0 = domingo, 6 = sábado
    return {
      viewMode: 'semana',
      semanaReferencia: startOfWeek(hoje, { weekStartsOn: 0 }),
      mesAtual: new Date(),
      diaSelecionadoMes: null,
      mostrarFinsDeSemana: diaDaSemana === 0 || diaDaSemana === 6,
    };
  },
  created() {
    this.emitPeriodoAtual();
  },
  computed: {
    locale() {
      return ptBR;
    },
    // Semana: domingo (0) a sábado (6)
    inicioSemana() {
      return startOfWeek(this.semanaReferencia, { weekStartsOn: 0 });
    },
    fimSemana() {
      return addDays(this.inicioSemana, 6);
    },
    periodoSemanaTexto() {
      return `${this.formatarData(this.inicioSemana, 'd MMM')} – ${this.formatarData(this.fimSemana, 'd MMM yyyy')}`;
    },
    inicioExibido() {
      return this.mostrarFinsDeSemana ? this.inicioSemana : addDays(this.inicioSemana, 1);
    },
    fimExibido() {
      return this.mostrarFinsDeSemana ? addDays(this.inicioSemana, 6) : addDays(this.inicioSemana, 5);
    },
    diasDaSemana() {
      return eachDayOfInterval({ start: this.inicioExibido, end: this.fimExibido });
    },
    eventosPorDia() {
      const map = new Map();
      this.diasDaSemana.forEach((dia) => {
        const inicio = startOfDay(dia);
        const fim = endOfDay(dia);
        const list = this.eventos
          .filter((e) => {
            const d = e.start instanceof Date ? e.start : new Date(e.start);
            return e.allDay ? isSameDay(d, dia) : isWithinInterval(d, { start: inicio, end: fim });
          })
          .sort((a, b) => this.ordenarEventos(a, b));
        map.set(dia.getTime(), list);
      });
      return map;
    },
    inicioMes() {
      return startOfMonth(this.mesAtual);
    },
    fimMes() {
      return endOfMonth(this.mesAtual);
    },
    eventosDoMes() {
      return this.eventos.filter((e) => {
        const d = e.start instanceof Date ? e.start : new Date(e.start);
        return isWithinInterval(d, { start: this.inicioMes, end: this.fimMes });
      });
    },
    eventosDoDiaSelecionado() {
      if (!this.diaSelecionadoMes) return [];
      const dia = startOfDay(this.diaSelecionadoMes);
      const fim = endOfDay(this.diaSelecionadoMes);
      return this.eventos
        .filter((e) => {
          const d = e.start instanceof Date ? e.start : new Date(e.start);
          return e.allDay ? isSameDay(d, this.diaSelecionadoMes) : isWithinInterval(d, { start: dia, end: fim });
        })
        .sort((a, b) => this.ordenarEventos(a, b));
    },
    gradeDoMes() {
      const inicio = startOfMonth(this.mesAtual);
      const fim = endOfMonth(this.mesAtual);
      const primeiroDia = startOfWeek(inicio, { weekStartsOn: 0 });
      const ultimoDia = endOfWeek(fim, { weekStartsOn: 0 });
      const dias = eachDayOfInterval({ start: primeiroDia, end: ultimoDia });
      const hoje = startOfDay(new Date());
      let id = 0;
      return dias.map((d) => {
        const vazio = !isSameMonth(d, this.mesAtual);
        const dia = getDate(d);
        const eventosNesteDia = vazio
          ? 0
          : this.eventosDoMes.filter((e) => {
            const start = e.start instanceof Date ? e.start : new Date(e.start);
            return isSameDay(start, d);
          }).length;
        return {
          id: ++id,
          vazio,
          dia,
          date: vazio ? null : d,
          hoje: !vazio && isSameDay(d, hoje),
          eventos: eventosNesteDia || null,
        };
      });
    },
  },
  methods: {
    isDiaSelecionado(date) {
      return date && this.diaSelecionadoMes && isSameDay(date, this.diaSelecionadoMes);
    },
    selecionarDiaMes(date) {
      this.diaSelecionadoMes = date;
      this.emitPeriodoAtual();
    },
    mudarView(mode) {
      this.viewMode = mode;
      if (mode === 'mes') {
        this.aplicarHojeSeMesAtual();
      } else {
        this.semanaReferencia = startOfWeek(new Date(), { weekStartsOn: 0 });
      }
      this.emitPeriodoAtual();
    },
    aplicarHojeSeMesAtual() {
      const hoje = startOfDay(new Date());
      if (isSameMonth(this.mesAtual, hoje)) {
        this.diaSelecionadoMes = hoje;
      } else {
        this.diaSelecionadoMes = startOfDay(this.inicioMes);
      }
    },
    emitPeriodoAtual() {
      if (this.viewMode === 'semana') {
        this.$emit('periodo-change', {
          tipo: 'semana',
          inicio: this.inicioExibido,
          fim: this.fimExibido,
        });
      } else if (this.viewMode === 'mes' && this.diaSelecionadoMes) {
        this.$emit('periodo-change', {
          tipo: 'dia',
          data: this.diaSelecionadoMes,
        });
      }
    },
    mesAnterior() {
      this.mesAtual = subMonths(this.mesAtual, 1);
      const hoje = startOfDay(new Date());
      this.diaSelecionadoMes = isSameMonth(this.mesAtual, hoje) ? hoje : startOfDay(this.inicioMes);
      this.emitPeriodoAtual();
    },
    proximoMes() {
      this.mesAtual = addMonths(this.mesAtual, 1);
      const hoje = startOfDay(new Date());
      this.diaSelecionadoMes = isSameMonth(this.mesAtual, hoje) ? hoje : startOfDay(this.inicioMes);
      this.emitPeriodoAtual();
    },
    formatarData(d, fmt) {
      const date = d instanceof Date ? d : new Date(d);
      return format(date, fmt, { locale: this.locale });
    },
    formatarHora(d) {
      const date = d instanceof Date ? d : new Date(d);
      return format(date, 'HH:mm', { locale: this.locale });
    },
    textoHorario(ev) {
      return ev.allDay ? 'Dia todo' : this.formatarHora(ev.start);
    },
    ordenarEventos(a, b) {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      return new Date(a.start) - new Date(b.start);
    },
    isHoje(dia) {
      return isSameDay(dia, new Date());
    },
    semanaAnterior() {
      this.semanaReferencia = addDays(this.semanaReferencia, -7);
      this.emitPeriodoAtual();
    },
    proximaSemana() {
      this.semanaReferencia = addDays(this.semanaReferencia, 7);
      this.emitPeriodoAtual();
    },
  },
};
</script>
<style scoped>
.calendario-simples {
  font-size: 0.9rem;
  color: var(--cor-fonte);
}

/* Linha título + toggle à direita */
.calendario-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.calendario-titulo {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--cor-primaria);
}

/* Toolbar estilo Google Calendar */
.calendario-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.calendario-view-toggle {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: var(--cor-cinza2, rgba(0, 0, 0, 0.06));
  border: 1px solid var(--cor-separador);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.view-btn {
  border: none;
  background: transparent;
  color: var(--cor-fonte);
  padding: 0.45rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.view-btn:hover:not(.ativo) {
  color: var(--cor-fonte-forte);
  background: rgba(255, 255, 255, 0.5);
}

.view-btn.ativo {
  background: var(--cor-primaria);
  color: var(--cor-bg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.calendario-finsemana-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--cor-fonte);
  user-select: none;
}

.calendario-finsemana-toggle input {
  width: 1rem;
  height: 1rem;
  accent-color: var(--cor-primaria);
  cursor: pointer;
}

.calendario-finsemana-label {
  color: var(--cor-fonte-fraca);
}

.calendario-nav-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calendario-nav-titulo {
  font-weight: 600;
  color: var(--cor-primaria);
  min-width: 140px;
  text-align: center;
  text-transform: capitalize;
}

.calendario-dia-titulo,
.calendario-semana-header {
  font-weight: 600;
  color: var(--cor-primaria);
  text-transform: capitalize;
}

.calendario-semana-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.calendario-semana-setas {
  border: none;
  background: transparent;
  color: var(--cor-fonte);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0.25rem 0.5rem;
}

.calendario-semana-setas:hover {
  color: var(--cor-primaria);
}

.lista-eventos {
  list-style: none;
  padding: 0;
  margin: 0;
}

.evento-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--cor-separador);
}

.evento-item:last-child {
  border-bottom: none;
}

.evento-hora,
.evento-data {
  flex-shrink: 0;
  color: var(--cor-fonte-fraca);
  font-size: 0.85em;
}

.evento-dia-todo .evento-hora {
  color: var(--cor-primaria);
  font-weight: 500;
}

.evento-anexos-google {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
  flex-shrink: 0;
}

.anexo-google-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.95rem;
  transition: background 0.15s, transform 0.15s;
}

.anexo-google-icone.tipo-gravacao {
  color: #dc2626;
}

.anexo-google-icone.tipo-transcricao {
  color: #2563eb;
}

.anexo-google-icone:hover {
  background: rgba(0, 0, 0, 0.06);
  transform: scale(1.1);
}

.evento-titulo {
  flex: 1;
}

.evento-clicavel {
  cursor: pointer;
}

.evento-clicavel:hover {
  background: var(--cor-cinza2, rgba(0, 0, 0, 0.06));
  border-radius: 4px;
}

.sem-eventos,
.sem-eventos-inline {
  color: var(--cor-fonte-fraca);
  font-size: 0.9em;
  margin: 0;
}

.sem-eventos-inline {
  padding: 0.25rem 0;
}

/* Semana em layout vertical */
.dia-semana {
  margin-bottom: 0.75rem;
}

.dia-semana-data {
  font-weight: 600;
  color: var(--cor-fonte);
  margin-bottom: 0.25rem;
  text-transform: capitalize;
}

.dia-semana-hoje .dia-semana-data {
  color: var(--cor-primaria);
}

.dia-semana-hoje-badge {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--cor-primaria);
}

.lista-eventos-compacta .evento-item {
  padding: 0.2rem 0;
}

/* Vista mês */
.calendario-nav {
  border: none;
  background: transparent;
  color: var(--cor-fonte);
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.25rem 0.5rem;
  line-height: 1;
}

.calendario-nav:hover {
  color: var(--cor-primaria);
}

.calendario-mes-wrap {
  max-width: 100%;
  margin-bottom: 0.75rem;
}

.calendario-mes-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.mes-semana-cabecalho {
  text-align: center;
  font-size: 0.7rem;
  color: var(--cor-fonte-fraca);
  padding: 0.2rem 0;
}

.mes-dia {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  position: relative;
  min-height: 24px;
}

.mes-dia-clicavel {
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0;
}

.mes-dia-clicavel:hover {
  background: var(--cor-cinza2);
}

.mes-dia-selecionado {
  outline: 2px solid var(--cor-primaria);
  outline-offset: 1px;
}

.mes-dia-vazio {
  visibility: hidden;
}

.mes-dia-numero {
  font-size: 0.8rem;
}

.mes-dia-hoje {
  background: var(--cor-primaria);
  color: var(--cor-bg);
}

.mes-dia-com-evento .mes-dia-badge {
  position: absolute;
  bottom: 2px;
  font-size: 0.6rem;
  background: var(--cor-primaria);
  color: var(--cor-bg);
  border-radius: 50%;
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mes-dia-hoje .mes-dia-badge {
  background: var(--cor-bg);
  color: var(--cor-primaria);
}

.mes-eventos-dia {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--cor-separador);
}

.mes-eventos-dia-titulo {
  font-weight: 600;
  color: var(--cor-primaria);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: capitalize;
}
</style>
